import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  DataTableCellContent,
  Text,
  createColumnHelper,
  type DataTableColumnDef,
} from "@salestrak/ui";
import {
  customFieldValuesEqual,
  dataToField,
  fieldDto,
} from "@salestrak/custom-fields";
import type { IssueRow } from "#app/lib/data";
import {
  CustomFieldValueCell,
} from "#app/components/custom-fields/custom-field-value-cell";
import { getCustomFieldDisplayValue } from "#app/components/custom-fields/custom-field-renderers";
import { NONE_OPTION_LABEL } from "#app/lib/custom-field-utils";
import type { CustomFieldDef } from "#app/components/custom-fields/types";
import type { CommitCustomFieldValue } from "#app/hooks/issue-view/use-custom-field-value-commit";
import {
  useOptionPickerOps,
  type OptionActions,
} from "#app/lib/hooks/use-custom-field-mutations";
import type { OnCreateOption } from "#app/components/custom-fields/option-picker";

/**
 * Custom-field columns for the shared {@link IssuesTable}: one opt-in column
 * per workspace field, whose cells view and edit the value through the same
 * {@link CustomFieldValueCell} the issue view dialog renders. The host keeps
 * them hidden by default (`useTableViewSettings` `defaultHidden`) — a
 * workspace can carry dozens of fields.
 */

const h = createColumnHelper<IssueRow>();

/** Column id prefix — stable across field renames, collision-free with the
 *  built-in column ids, and what the persisted view settings key on. */
export const ISSUE_CF_COLUMN_PREFIX = "cf:";

export function issueCustomFieldColumnId(customFieldId: string): string {
  return `${ISSUE_CF_COLUMN_PREFIX}${customFieldId}`;
}

/** Width floor per field type. The default 160 fits the plain inline editors;
 *  phone's editor leads with the country selector (flag + dial code +
 *  chevron ≈ 90px) before the number input, so at 160 a formatted number
 *  shows only a few characters. An unset `width` resolves to `minWidth`, so
 *  the floor is also the column's opening width. */
const CF_COLUMN_MIN_WIDTH: Partial<Record<CustomFieldDef["type"], number>> = {
  phone: 220,
};

const CF_COLUMN_DEFAULT_MIN_WIDTH = 160;

function IssueCustomFieldCell({
  issue,
  field,
  commit,
  onCreateOption,
  optionActions,
}: {
  issue: IssueRow;
  field: CustomFieldDef;
  commit: CommitCustomFieldValue;
  onCreateOption: OnCreateOption;
  optionActions: OptionActions | undefined;
}) {
  const readOnly = !issue.viewerCanEdit;
  const [isActive, setIsActive] = useState(false);
  // Mirror of the cell's overlay/touch-reveal state; feeds `highlighted` so
  // the cell's own dismiss lifecycle works without a dialog-style container.
  const [overlayOpen, setOverlayOpen] = useState(false);

  const rawValue: unknown = issue.values?.[field.customFieldId];
  // Field-space conversion, exactly as the issue view's formValues does it.
  // An absent value stays `undefined` — the cell renders its empty marker.
  const value = useMemo(
    () =>
      rawValue === undefined
        ? undefined
        : dataToField(field.type, rawValue).fieldValue,
    [field.type, rawValue],
  );

  const handleUpdate = useCallback(
    (next: unknown) => {
      if (next === undefined) {
        commit(issue, field.customFieldId, undefined);
        return;
      }
      // fieldDto coerces rather than throws; commit surfaces its own errors.
      const dto = fieldDto(field.type, next);
      // The cell commits on every blur, edited or not: the value the row
      // already holds is not a write.
      if (customFieldValuesEqual(rawValue, dto)) return;
      commit(issue, field.customFieldId, dto);
    },
    [commit, issue, field, rawValue],
  );

  const handleActivate = useCallback(() => setIsActive(true), []);
  const handleDeactivate = useCallback(() => setIsActive(false), []);

  // Photos stay display-only here: the upload grid grows row height, which
  // the windowed virtual scroll cannot take. Editing photos lives in the
  // issue view dialog.
  if (field.type === "photos") {
    const display = getCustomFieldDisplayValue(field, value);
    return (
      <DataTableCellContent>
        <Text
          size="sm"
          variant={display === NONE_OPTION_LABEL ? "faint" : undefined}
          truncate={1}
        >
          {display}
        </Text>
      </DataTableCellContent>
    );
  }

  return (
    // The 2px vertical inset floats the hover/edit ring inside the row — flush
    // against the row hairlines it merges with them and disappears (the same
    // reason DataTableCellTrigger wears my-0.5). The cell fills the rest, so
    // it is the row minus that inset at every density, matching the sibling
    // popover-cell triggers.
    <Box fill="flex" minWidth={0} direction="row" align="stretch" paddingY="2xs">
      <CustomFieldValueCell
        field={field}
        value={value}
        isActive={isActive}
        // A table cell hard-clips: the row-style end bleed would cut the
        // outline's corners square.
        bleedEnd={false}
        highlighted={overlayOpen || isActive}
        onUpdate={handleUpdate}
        onOverlayChange={setOverlayOpen}
        onActivate={handleActivate}
        onDeactivate={handleDeactivate}
        readOnly={readOnly}
        downloadPrefix={issue.name}
        onCreateOption={onCreateOption}
        optionActions={optionActions}
      />
    </Box>
  );
}

export function useIssueCustomFieldColumns({
  fields,
  commit,
}: {
  fields: CustomFieldDef[];
  commit: CommitCustomFieldValue;
}): DataTableColumnDef<IssueRow>[] {
  // Hoisted: the picker ops hold mutations, and a cell renders once per row.
  const { onCreateOption, optionActions } = useOptionPickerOps();
  return useMemo(
    () =>
      fields.map((field) =>
        h.display({
          id: issueCustomFieldColumnId(field.customFieldId),
          // Plain string: the column settings panel and header-drag ghost
          // derive their labels from string headers.
          header: field.name,
          meta: {
            minWidth:
              CF_COLUMN_MIN_WIDTH[field.type] ?? CF_COLUMN_DEFAULT_MIN_WIDTH,
            maxWidth: 320,
            // The kanban card field token's read-out: which field this is,
            // and that it is a custom one — built-in columns need no such
            // orientation, so only these carry the tooltip.
            headerTooltip: { title: field.name, subtitle: "Custom Field" },
          },
          cell: ({ row }) => (
            <IssueCustomFieldCell
              issue={row.original}
              field={field}
              commit={commit}
              onCreateOption={onCreateOption}
              optionActions={optionActions}
            />
          ),
        }),
      ),
    [fields, commit, onCreateOption, optionActions],
  );
}
